import type { DomainTexts, DomainText, Identifier } from '../types/PersologOnlineTypes'
import type { MaintenanceItem } from '../types/TextMaintenanceTypes'
import { LanguageManagementAPI } from '../api'

/**
 * Builds complete DomainTexts for a language by merging the raw (fully-loaded) cache
 * with any changes recorded in maintenance items.
 * This ensures that saving a language that wasn't fully displayed in the UI never
 * sends a partial dataset and accidentally wipes untouched entries.
 */
/**
 * Builds a lookup of identifier → changed text from maintenance items, plus the set of
 * identifiers whose text was deleted for this language so they can be dropped entirely.
 */
function collectLanguageChanges(
  language: string,
  maintenanceItems: MaintenanceItem[]
): { changedTexts: Map<string, string>; deletedIdentifiers: Set<string> } {
  const changedTexts = new Map<string, string>();
  const deletedIdentifiers = new Set<string>();
  for (const item of maintenanceItems) {
    if (language.startsWith('__')) continue;
    const changedForLanguage = item.changedTexts?.[language];
    if ((changedForLanguage || item.isNew) && item.texts[language] !== undefined) {
      changedTexts.set(item.identifier, item.texts[language]);
    } else if (changedForLanguage && item.texts[language] === undefined) {
      // The text was removed for this language: exclude the identifier from the saved
      // dataset instead of letting it fall back to the (stale) raw value below.
      deletedIdentifiers.add(item.identifier);
    }
  }
  return { changedTexts, deletedIdentifiers };
}

/** Appends brand-new identifiers that exist only in maintenance items (not in raw data yet). */
function appendNewIdentifiers(
  domainTexts: DomainText[],
  language: string,
  rawIdentifiers: Identifier[],
  maintenanceItems: MaintenanceItem[]
): void {
  for (const item of maintenanceItems) {
    if (item.isNew && item.texts[language]) {
      const alreadyIncluded = rawIdentifiers.some(r => r.identifier === item.identifier);
      if (!alreadyIncluded) {
        domainTexts.push({ identifier: item.identifier, text: item.texts[language] });
      }
    }
  }
}

export function buildDomainTextsFromRaw(
  language: string,
  rawIdentifiers: Identifier[],
  maintenanceItems: MaintenanceItem[]
): DomainTexts {
  const { changedTexts, deletedIdentifiers } = collectLanguageChanges(language, maintenanceItems);

  // Start from the full raw dataset for this language, patching in any changed values
  // and dropping identifiers that were deleted for this language.
  const domainTexts: DomainText[] = [];
  for (const identifier of rawIdentifiers) {
    if (deletedIdentifiers.has(identifier.identifier)) continue;
    const rawText = identifier.texts.find(t => t.language === language);
    const changedText = changedTexts.get(identifier.identifier);
    const text = changedText ?? rawText?.text;
    if (text !== undefined && text !== '') {
      domainTexts.push({ identifier: identifier.identifier, text });
    }
  }

  appendNewIdentifiers(domainTexts, language, rawIdentifiers, maintenanceItems);

  return { domainTexts };
}

/**
 * Converts maintenance data to DomainTexts format for saving to backend
 * @param language - The language code
 * @param items - Array of maintenance items
 * @returns DomainTexts object ready for API
 */
export function convertToDomainTexts(
  language: string,
  items: MaintenanceItem[]
): DomainTexts {
  // Include ALL items that have text for this language, not just changed ones
  // The API replaces all texts for the language, so we need to send everything
  const domainTexts: DomainText[] = items
    .filter(item => item.texts[language] !== undefined && item.texts[language] !== '')
    .map(item => ({
      identifier: item.identifier,
      text: item.texts[language]
    }));

  return {
    domainTexts: domainTexts
  };
}

/**
 * Helper function to determine the correct product ID
 */
function getProductId(items: MaintenanceItem[], domain: 'profile' | 'questionnaire', baseProductId: string): string {
  // If there are no items, construct the expected product ID
  if (items.length === 0) {
    return `${domain}-${baseProductId}`;
  }

  // Try to infer from the first item's ID structure
  const firstItemId = items[0].id;
  if (firstItemId.includes('-')) {
    // Extract product part from item ID (e.g., "profile-in100-some.identifier" -> "profile-in100")
    const parts = firstItemId.split('-');
    if (parts.length >= 2) {
      return `${parts[0]}-${parts[1]}`;
    }
  }

  // Fallback to constructed ID
  return `${domain}-${baseProductId}`;
}

/**
 * Collects all language codes that have pending changes across the given items.
 * This includes languages tracked in changedTexts and languages present in newly added items.
 */
export function extractLanguagesWithChanges(items: MaintenanceItem[]): string[] {
  const languages = new Set<string>();
  for (const item of items) {
    collectNewItemLanguages(item, languages);
    collectChangedLanguages(item, languages);
  }
  return Array.from(languages);
}

function collectNewItemLanguages(item: MaintenanceItem, languages: Set<string>) {
  if (!item.isNew) return;
  for (const [lang, text] of Object.entries(item.texts)) {
    if (!lang.startsWith('__') && text) languages.add(lang);
  }
}

function collectChangedLanguages(item: MaintenanceItem, languages: Set<string>) {
  if (!item.changedTexts) return;
  for (const [lang, changed] of Object.entries(item.changedTexts)) {
    if (!lang.startsWith('__') && changed) languages.add(lang);
  }
}

/**
 * Check if there are any changes for a specific language
 */
function hasChangesForLanguage(items: MaintenanceItem[], language: string): boolean {
  if (language.startsWith('__')) return false;
  return items.some(item => 
    (item.isNew && item.texts[language]) || 
    item.changedTexts?.[language]
  );
}

interface SaveEntry {
  product: string;
  language: string;
  domainTexts: DomainText[];
}

/**
 * Builds the list of (namespace, language) payloads that actually have changes.
 * Mirrors the previous per-language logic, but collected up-front so the whole set
 * can be sent in a single request.
 */
function buildSaveEntries(
  domains: Array<{ items: MaintenanceItem[]; productId: string; raw?: Identifier[] }>,
  languages: string[]
): SaveEntry[] {
  const entries: SaveEntry[] = [];
  for (const domain of domains) {
    if (domain.items.length === 0) continue;
    for (const language of languages) {
      if (!hasChangesForLanguage(domain.items, language)) continue;
      const domainTexts = domain.raw
        ? buildDomainTextsFromRaw(language, domain.raw, domain.items)
        : convertToDomainTexts(language, domain.items);
      entries.push({ product: domain.productId, language, domainTexts: domainTexts.domainTexts });
    }
  }
  return entries;
}

/** Fallback: one request per entry (in parallel) when the bulk endpoint is unavailable. */
async function saveEntriesPerLanguage(entries: SaveEntry[], errors: string[]): Promise<number> {
  const results = await Promise.all(
    entries.map(async (entry) => {
      try {
        await LanguageManagementAPI.saveAllTextsForProduct(entry.product, entry.language, {
          domainTexts: entry.domainTexts,
        });
        return true;
      } catch (error) {
        errors.push(`Failed to save ${entry.product} for language ${entry.language}: ${error}`);
        return false;
      }
    })
  );
  return results.filter(Boolean).length;
}

/**
 * Saves all changed texts across every namespace and language in a SINGLE request
 * (the bulk endpoint persists them in one backend transaction). Falls back to one
 * request per (namespace, language) if the bulk endpoint isn't available.
 * @param baseProductId - The base product ID (e.g., "in100")
 * @param languages - Array of language codes to consider
 * @param profileItems - Profile maintenance items
 * @param questionnaireItems - Questionnaire maintenance items
 * @param defaultItems - Default/texts maintenance items (optional)
 */
export async function saveAllChangesToBackend(
  baseProductId: string,
  languages: string[],
  profileItems: MaintenanceItem[],
  questionnaireItems: MaintenanceItem[],
  defaultItems?: MaintenanceItem[],
  rawTexts?: { profile: Identifier[]; questionnaire: Identifier[]; default: Identifier[] }
): Promise<{ success: boolean; savedCount: number; errors: string[] }> {
  const errors: string[] = [];

  try {
    const entries = buildSaveEntries(
      [
        { items: profileItems, productId: getProductId(profileItems, 'profile', baseProductId), raw: rawTexts?.profile },
        { items: questionnaireItems, productId: getProductId(questionnaireItems, 'questionnaire', baseProductId), raw: rawTexts?.questionnaire },
        { items: defaultItems ?? [], productId: baseProductId, raw: rawTexts?.default },
      ],
      languages
    );

    if (entries.length === 0) {
      return { success: true, savedCount: 0, errors: [] };
    }

    // One request for everything. Fall back to one request per (namespace, language)
    // only if the bulk endpoint is unavailable (older backend).
    try {
      await LanguageManagementAPI.saveAllTextsForProductBulk(baseProductId, entries);
      return { success: true, savedCount: entries.length, errors: [] };
    } catch (bulkError) {
      console.warn('Bulk save unavailable; falling back to per-language saves.', bulkError);
      const savedCount = await saveEntriesPerLanguage(entries, errors);
      return { success: errors.length === 0, savedCount, errors };
    }
  } catch (error) {
    errors.push(`Unexpected error during save operation: ${error}`);
    return {
      success: false,
      savedCount: 0,
      errors
    };
  }
}

/**
 * Creates new product namespace(s) using the API
 * @param productId - The base product ID (e.g., "in100")
 * @param language - The language code (e.g., "de", "en")
 * @param createBothNamespaces - Whether to create both profile and questionnaire namespaces
 * @returns Promise that resolves when all products are created
 */
export async function createNewProductNamespace(
  productId: string,
  language: string,
  createBothNamespaces: boolean = false
): Promise<void> {
  const emptyDomainTexts: DomainTexts = {
    domainTexts: []
  };

  if (createBothNamespaces) {
    // Create both profile and questionnaire namespaces
    await LanguageManagementAPI.saveAllTextsForProduct(`profile-${productId}`, language, emptyDomainTexts);
    await LanguageManagementAPI.saveAllTextsForProduct(`questionnaire-${productId}`, language, emptyDomainTexts);
  } else {
    // Create single namespace without prefix
    await LanguageManagementAPI.saveAllTextsForProduct(productId, language, emptyDomainTexts);
  }
}
